import { Fragment, memo, useMemo, type CSSProperties, type ReactNode } from 'react'
import { Check } from 'lucide-react'
import type {
  Block,
  HeadingBlock,
  ImageBlock,
  LinkCardBlock,
  MarginBlock,
  MarginNoteBlock,
  PullQuoteBlock,
  SceneBlock,
  TextBlock,
  ThoughtBlock,
  ThoughtLikeBlock,
  UnfoldBlock,
} from '../../lib/types'
import {
  headingClass,
  headingLevel,
  headingTag,
  isEditable,
  isPinnedNote,
  pullquoteClass,
  quoteAttribution,
  isOutOfFlow,
  marginsByAnchor,
  pinnedRanges,
  pinSide,
  thoughtSourceLabel,
  unfoldAnchorsIn,
  upgradePins,
} from '../../lib/blocks'
import { hostOf, stripTags, thoughtStamp } from '../../lib/text'
import { VoiceMemoView } from '../audio/VoiceMemoView'
import { FramedImage } from '../FramedImage'
import { LinkSite } from '../LinkSite'
import { CollectionCardBody, collectionCardCls } from '../CollectionLinkCard'
import { collectionPageUrl } from '../../lib/urls'
import { CaptionOverlay } from '../CaptionOverlay'
import { imageLayout } from '../imageLayout'
import { cellMarginStyle, columnGroups, columnsGridStyle, normalizeColumns } from '../../lib/columns'
import { DARK_INK, DEFAULT_SCENE_COLOR, SCENE_SHADE, sceneInk } from '../../lib/sceneColors'
import { inlineHtmlNodes, plainTextNodes } from './InlineText'
import { noteHtml } from '../../lib/sanitize'
import { marginNoteId, unfoldCardId, useReader } from './context'

// The column and its margins (design 4b). Column blocks render in order;
// margin notes and voice memos hang beside the block above them (right),
// pinned margin notes stay beside their range (left or right, sticky), unfolds open under the words
// they hang from, scenes break out full-bleed. Below 1024px every aside
// falls back inline under its anchor (see .wr-aside-* in index.css).

const NO_MARKS: readonly string[] = []

type Segment = { kind: 'block'; block: Block } | { kind: 'pin'; pin: MarginNoteBlock; blocks: Block[] } | { kind: 'lone-pin'; pin: MarginNoteBlock }

function segment(blocks: Block[]): Segment[] {
  const ranges = pinnedRanges(blocks)
  const pinOf = new Map<string, MarginNoteBlock>()
  for (const b of blocks) if (isPinnedNote(b)) for (const id of ranges.get(b.id) ?? []) pinOf.set(id, b)
  const out: Segment[] = []
  for (const b of blocks) {
    if (isPinnedNote(b) && (ranges.get(b.id) ?? []).length === 0) {
      out.push({ kind: 'lone-pin', pin: b })
      continue
    }
    if (isOutOfFlow(b)) continue
    const pin = pinOf.get(b.id)
    const last = out[out.length - 1]
    if (pin && last?.kind === 'pin' && last.pin.id === pin.id) last.blocks.push(b)
    else if (pin) out.push({ kind: 'pin', pin, blocks: [b] })
    else out.push({ kind: 'block', block: b })
  }
  return out
}

interface BodyProps {
  blocks: Block[]
  /** The block being read (null: none, so nothing dims). */
  focalId: string | null
  /** Reading light: everything but the focal block dims. */
  dim: boolean
  /** Also kept lit: the photo a lit paragraph wraps around and runs on below (its box sits under the photo). */
  alsoLitId?: string | null
}

/** The document as runs of the page and Columns groups, each group's cells as their own runs. */
type Part = { kind: 'flow'; blocks: Block[] } | { kind: 'columns'; id: string; cells: Block[][] }

function parts(blocks: Block[]): Part[] {
  const out: Part[] = []
  let from = 0
  for (const g of columnGroups(blocks)) {
    out.push({ kind: 'flow', blocks: blocks.slice(from, g.start) })
    out.push({ kind: 'columns', id: g.startId, cells: g.cells.map((c) => blocks.slice(c.from, c.to)) })
    from = g.end + 1
  }
  out.push({ kind: 'flow', blocks: blocks.slice(from) })
  return out
}

export function ArticleBody({ blocks: raw, focalId, dim, alsoLitId = null }: BodyProps) {
  // Markers out of shape (hand-edited data, an old client) are repaired before anything reads them.
  const blocks = useMemo(() => normalizeColumns(upgradePins(raw)), [raw])
  const runs = useMemo(() => parts(blocks).map((p) => (p.kind === 'flow' ? { ...p, segments: segment(p.blocks) } : { ...p, cellSegments: p.cells.map(segment) })), [blocks])
  const margins = useMemo(() => marginsByAnchor(blocks), [blocks])
  const unit = (b: Block, inPin: boolean, inCell: boolean) => (
    <ColumnUnit
      key={b.id}
      block={b}
      margins={margins.get(b.id)}
      focal={focalId === b.id}
      dimmed={dim && focalId !== b.id && alsoLitId !== b.id && DIMMABLE.has(b.type)}
      inPin={inPin}
      inCell={inCell}
    />
  )
  const renderSegments = (segments: Segment[], inCell: boolean) =>
    segments.map((s) => {
      if (s.kind === 'block') return unit(s.block, false, inCell)
      if (s.kind === 'lone-pin') {
        return (
          <div key={s.pin.id} className="mb-6">
            <PinCard pin={s.pin} />
          </div>
        )
      }
      // On the right (the author's choice), the pin has that margin for its range: margin notes there fold under
      // their paragraphs (.wr-pin-right), and the left margin keeps its "Read from here".
      const right = pinSide(s.pin) === 'right'
      return (
        <div key={s.pin.id} className={`wr-anchor ${right ? 'wr-pin-right' : ''}`}>
          <div className={`${right ? 'wr-aside-right-track' : 'wr-aside-left-track'} wr-r-pin-track`}>
            <PinCard pin={s.pin} />
          </div>
          {s.blocks.map((b) => unit(b, !right, inCell))}
        </div>
      )
    })
  const orphans = margins.get('')
  return (
    <>
      {orphans && <MarginStack items={orphans} inline />}
      {runs.map((run, i) =>
        'segments' in run ? (
          <Fragment key={`flow-${i}`}>{renderSegments(run.segments, false)}</Fragment>
        ) : (
          // Columns: side by side from 560px wide, stacked below (index.css .wr-cols).
          <div key={run.id} className="wr-cols wr-r-cols">
            <div
              className="wr-cols-grid"
              data-n={run.cellSegments.length}
              style={columnsGridStyle(blocks.find((b) => b.id === run.id), run.cellSegments.length) as CSSProperties}
            >
              {run.cellSegments.map((segs, c) => (
                <div key={c} className="wr-cell" style={cellMarginStyle(blocks.find((b) => b.id === run.id), run.cellSegments.length, c) as CSSProperties}>
                  {renderSegments(segs, true)}
                </div>
              ))}
            </div>
          </div>
        ),
      )}
    </>
  )
}

/** Column blocks the Reading light dims when they're not the one being read. */
const DIMMABLE = new Set<Block['type']>(['text', 'heading', 'pullquote', 'thought', 'image', 'linkcard', 'collection', 'divider'])

interface FocusAttrs {
  'data-focus': string
  'data-focal'?: ''
  'data-dimmed'?: ''
}

function focusAttrs(id: string, focal: boolean, dimmed: boolean): FocusAttrs {
  return { 'data-focus': id, ...(focal ? { 'data-focal': '' } : {}), ...(dimmed ? { 'data-dimmed': '' } : {}) }
}

interface UnitProps {
  block: Block
  margins?: MarginBlock[]
  focal: boolean
  dimmed: boolean
  /** Inside a left pin's range: the left margin is the pin's, so no "Read from here" there. */
  inPin: boolean
  /** In a column cell: images don't wrap there (its margin items still go out in the page's margin). */
  inCell?: boolean
}

const ColumnUnit = memo(function ColumnUnit({ block, margins, focal, dimmed, inPin, inCell = false }: UnitProps) {
  const r = useReader()
  const focus = focusAttrs(block.id, focal, dimmed)
  const empty = isEditable(block) && !stripTags(block.html).trim()
  // In a column too, they go out in the page's margin (index.css .wr-cell); only a scene's sit under it.
  const asides = margins && margins.length > 0 ? <MarginStack items={margins} inline={block.type === 'scene'} /> : null

  if (block.type === 'scene') {
    return (
      <>
        <SceneView block={block} focus={focusAttrs(block.id, focal, false)} />
        {asides}
      </>
    )
  }
  // An empty paragraph still anchors its margin items (otherwise they'd be
  // positioned against the page and land off-screen on wide screens).
  if (empty) return asides ? <div className="wr-anchor">{asides}</div> : null

  let el: ReactNode
  switch (block.type) {
    case 'text':
      el = <TextView block={block} focus={focus} />
      break
    case 'heading':
      el = <HeadingView block={block} focus={focus} />
      break
    case 'pullquote':
      el = <PullQuoteView block={block} focus={focus} inCell={inCell} />
      break
    case 'thought':
      el = <ThoughtView block={block} focus={focus} />
      break
    case 'image':
      el = <ImageView block={block} focus={focus} inCell={inCell} />
      break
    case 'linkcard':
      el = <LinkCardView block={block} focus={focus} />
      break
    case 'collection': {
      // Only a collection that was public when picked has a page readers can open; a private one shows nothing.
      const href = collectionPageUrl(block.slug)
      if (!href) return asides ? <div className="wr-anchor">{asides}</div> : null
      el = (
        <a {...focus} href={href} aria-label={`Collection: ${block.name}`} className={`wr-r-dim my-8 ${collectionCardCls} [transition:opacity_var(--wr-light-fade),border-color_0.15s] hover:border-accent`}>
          <CollectionCardBody block={block} author={r.authorName} explore />
        </a>
      )
      break
    }
    case 'divider':
      el = (
        <div role="separator" className="wr-r-dim clear-both my-12 flex justify-center gap-2.5" {...(dimmed ? { 'data-dimmed': '' } : {})}>
          {[0, 1, 2].map((i) => (
            <span key={i} aria-hidden className="size-1 rounded-full bg-accent" />
          ))}
        </div>
      )
      break
    default:
      return null
  }

  const anchors = isEditable(block) ? unfoldAnchorsIn(block.html).filter((a) => r.openUnfolds.has(a) && r.unfolds.has(a)) : []
  const marker = !inPin && !inCell && (block.type === 'text' || block.type === 'heading' || block.type === 'thought' || block.type === 'pullquote')
  return (
    <div className="wr-anchor group/unit">
      {marker && <FocalMarker id={block.id} focal={focal} />}
      {el}
      {anchors.map((a) => (
        <UnfoldCard key={a} block={r.unfolds.get(a)!} />
      ))}
      {asides}
    </div>
  )
})

/**
 * Left-margin "Read from here" (wide only): while narration is under way, each
 * read block but the one being read offers it on hover. There's no "Now
 * reading" label: the Reading light's tint and the gutter bar mark the block
 * being read (Niki, 10/2).
 */
function FocalMarker({ id, focal }: { id: string; focal: boolean }) {
  const r = useReader()
  if (focal || !r.listening || !r.narrated.has(id)) return null
  return (
    <button
      type="button"
      onClick={() => r.seekTo(id)}
      title="Play the narration from here"
      className="absolute right-full top-2 mr-8 hidden items-center gap-1.5 whitespace-nowrap rounded-full px-1.5 py-0.5 font-sans text-[11px] font-medium text-muted-foreground/70 opacity-0 transition-opacity hover:bg-foreground/5 hover:text-foreground focus-visible:opacity-100 group-hover/unit:opacity-100 wide:flex"
    >
      <span aria-hidden className="size-1.5 rounded-full bg-accent" />
      Read from here
    </button>
  )
}

/* ------------------------------------------------------------ column blocks */

function useLineMarks(id: string): readonly string[] {
  return useReader().takes?.lines.get(id) ?? NO_MARKS
}

function useHtmlNodes(id: string, html: string): ReactNode[] {
  const r = useReader()
  const marks = useLineMarks(id)
  return useMemo(() => inlineHtmlNodes(html, marks, r.unfoldControls, r.notes), [html, marks, r.unfoldControls, r.notes])
}

function TextView({ block, focus }: { block: TextBlock; focus: FocusAttrs }) {
  const nodes = useHtmlNodes(block.id, block.html)
  return (
    <p data-take={block.id} {...focus} className="wr-prose wr-r-dim wr-r-lit wr-t-body m-0 mb-6">
      {nodes}
    </p>
  )
}

function HeadingView({ block, focus }: { block: HeadingBlock; focus: FocusAttrs }) {
  const nodes = useHtmlNodes(block.id, block.html)
  const level = headingLevel(block)
  const Tag = headingTag(level)
  return (
    <Tag data-take={block.id} {...focus} className={`wr-prose wr-r-dim wr-r-lit ${headingClass(level)} m-0 mb-4 ${level === 3 ? 'mt-9' : 'mt-11'}`}>
      {nodes}
    </Tag>
  )
}

function takenBy(n: number): string {
  return `Taken by ${n} reader${n === 1 ? '' : 's'}`
}

/**
 * A pull quote: the words, who said them when the author says (no dash
 * typed into the quote needed: "— Steven Johnson" is drawn here), and the
 * Take pill under them (Niki, 10/6). Taking it makes a card crediting the
 * quoted person, via this writing (save_writing_quote).
 */
function PullQuoteView({ block, focus, inCell = false }: { block: PullQuoteBlock; focus: FocusAttrs; inCell?: boolean }) {
  const { takes } = useReader()
  const nodes = useHtmlNodes(block.id, block.html)
  const said = quoteAttribution(block)
  return (
    <aside
      id={`q-${block.id}`}
      {...focus}
      // Out into the margins in the column; within its cell in columns.
      className={`wr-r-dim group/quote my-11 flex scroll-mt-24 flex-col items-center px-2 text-center ${inCell ? '' : 'md:-mx-[60px] md:px-10'}`}
    >
      <p data-take={block.id} className={`wr-prose ${pullquoteClass(headingLevel(block))} m-0 [text-wrap:pretty]`}>
        {nodes}
      </p>
      {said && (
        <p className="m-0 mt-3.5 font-sans text-[13.5px] font-medium tracking-[0.01em] text-foreground/70">
          <span aria-hidden>— </span>
          <cite className="not-italic">{said}</cite>
        </p>
      )}
      {takes && <QuoteTake id={block.id} />}
    </aside>
  )
}

/**
 * The pull quote's Take: a quiet pill in the rose of the selection toolbar's
 * Take (its blush dot), half there until the quote is hovered or focused and
 * always there on touch. Taken, it fills with the primary and a check; how
 * many readers took the quote sits beside it.
 */
function QuoteTake({ id }: { id: string }) {
  const takes = useReader().takes!
  const count = takes.counts[id] ?? 0
  const mine = takes.quotes.has(id)
  const busy = takes.busyId === id
  const shown = mine ? 'opacity-100' : 'opacity-55 group-hover/quote:opacity-100 group-focus-within/quote:opacity-100 [@media(hover:none)]:opacity-100'
  return (
    <div className={`mt-4 flex items-center gap-2.5 font-sans transition-opacity duration-200 ${shown}`}>
      <button
        type="button"
        disabled={mine || busy}
        onClick={() => takes.takeQuote(id)}
        aria-label={mine ? 'Taken' : 'Take this quote'}
        className={`inline-flex h-7 items-center gap-1.5 rounded-full border px-3 text-[12.5px] font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 disabled:cursor-default ${
          mine
            ? 'border-primary bg-primary text-primary-foreground'
            : 'border-primary/25 bg-card/60 text-primary hover:border-primary/60 hover:bg-accent/10 disabled:opacity-70'
        }`}
      >
        {mine ? (
          <Check aria-hidden className="size-3.5" strokeWidth={2.5} />
        ) : (
          <span aria-hidden className="size-2 rounded-full border-[1.5px] border-primary bg-accent/15" />
        )}
        {mine ? 'Taken' : busy ? 'Taking…' : 'Take quote'}
      </button>
      {count > 0 && <span className="text-[12px] font-medium text-muted-foreground">{takenBy(count)}</span>}
    </div>
  )
}

function ThoughtView({ block, focus }: { block: ThoughtBlock; focus: FocusAttrs }) {
  const r = useReader()
  const { takes } = r
  const marks = useLineMarks(block.id)
  const nodes = useMemo(() => plainTextNodes(block.text, marks), [block.text, marks])
  const count = takes?.counts[block.id] ?? 0
  const active = r.activeQuote === block.id
  const site = hostOf(block.source?.url)
  const sourceTitle = block.source?.title || site
  return (
    <figure
      id={`q-${block.id}`}
      {...focus}
      tabIndex={takes ? 0 : undefined}
      onClick={takes ? () => r.setActiveQuote(active ? null : block.id) : undefined}
      className="wr-r-dim group relative my-8 flow-root scroll-mt-24 rounded-2xl border border-card-edge bg-card px-5 pb-4 pt-[18px] outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
    >
      {takes && <QuoteBar block={block} visible={active} />}
      {r.showTimes && block.capturedAt && (
        <div className="mb-2 font-sans text-[11.5px] font-medium text-muted-foreground">
          {thoughtStamp(block.capturedAt)}
          {block.kind ? ` · ${block.kind}` : ''}
        </div>
      )}
      <blockquote data-take={block.id} className="wr-t-thought m-0 mb-2.5 text-foreground">
        {nodes}
      </blockquote>
      {(sourceTitle || count > 0) && (
        <figcaption className="flex flex-wrap items-center gap-x-2 gap-y-1 font-sans text-[13px] font-medium text-muted-foreground">
          {sourceTitle && (
            <>
              <span aria-hidden className="size-1.5 rounded-full bg-accent" />
              {block.source?.url ? (
                <a
                  href={block.source.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="text-muted-foreground no-underline hover:underline"
                >
                  {sourceTitle}
                </a>
              ) : (
                <span>{sourceTitle}</span>
              )}
              {site && site !== sourceTitle && <span className="text-muted-foreground/70">{site}</span>}
            </>
          )}
          {count > 0 && <span className="ml-auto text-muted-foreground">{takenBy(count)}</span>}
        </figcaption>
      )}
    </figure>
  )
}

function QuoteBar({ block, visible }: { block: ThoughtBlock; visible: boolean }) {
  const takes = useReader().takes!
  const mine = takes.quotes.has(block.id)
  const busy = takes.busyId === block.id
  const item = 'whitespace-nowrap rounded-[5px] px-2.5 py-[5px] hover:bg-card/10'
  return (
    <div
      role="toolbar"
      aria-label="Quote actions"
      onClick={(e) => e.stopPropagation()}
      className={`absolute -top-[40px] left-6 z-10 gap-0.5 rounded-lg bg-foreground p-1 font-sans text-[13px] font-medium text-card shadow-[var(--shadow-chip)] ${
        visible ? 'flex' : 'hidden group-focus-within:flex group-hover:flex'
      }`}
    >
      <button
        type="button"
        disabled={mine || busy}
        onClick={() => takes.takeQuote(block.id)}
        aria-label={mine ? 'Taken' : 'Take this quote'}
        className={`${item} flex items-center gap-1.5 bg-card/15 disabled:cursor-default`}
      >
        <span aria-hidden className="size-2 rounded-full bg-accent/15" />
        {mine ? 'Taken' : busy ? 'Taking…' : 'Take'}
      </button>
      <button type="button" onClick={() => takes.copyQuote(block)} className={item}>
        Copy
      </button>
      <button type="button" onClick={() => takes.shareQuote(block)} className={item}>
        Share
      </button>
    </div>
  )
}

/**
 * An image at its placement and width (components/imageLayout.ts): wraps
 * float from tablet up (the text wraps them) and sit in the flow on phones;
 * z-[1] keeps a float above the focal paragraph's tint, which paints in a
 * later layer. The caption sits over the photo's bottom-left corner; it has
 * no data-take, so a reader's "take a line" never picks it up.
 */
function ImageView({ block, focus, inCell = false }: { block: ImageBlock; focus: FocusAttrs; inCell?: boolean }) {
  const { showTimes } = useReader()
  const layout = imageLayout(block, { cell: inCell })
  const stamp = showTimes && block.capturedAt ? thoughtStamp(block.capturedAt) : null
  const space = block.placement === 'full' ? 'my-9' : 'mb-8'
  return (
    <figure {...focus} style={layout.style} className={`wr-r-dim relative m-0 flex flex-col ${space} ${layout.figure}`}>
      <FramedImage
        src={block.src}
        alt={block.caption}
        frame={block.frame}
        fit={layout.fit}
        defaultPosition="center 60%"
        imgClassName={layout.img}
        className={`rounded-[4px] bg-muted ${layout.box}`}
      />
      <CaptionOverlay caption={block.caption} stamp={stamp} small={layout.wrap} />
    </figure>
  )
}

function LinkCardView({ block, focus }: { block: LinkCardBlock; focus: FocusAttrs }) {
  return (
    <a
      {...focus}
      href={block.url}
      target="_blank"
      rel="noopener noreferrer"
      className="wr-r-dim group my-8 flex flex-col gap-1.5 rounded-2xl border border-card-edge bg-card px-5 py-4 text-foreground no-underline [transition:opacity_var(--wr-light-fade),box-shadow_0.15s] hover:shadow-lg hover:shadow-accent/5"
    >
      <LinkSite url={block.url} />
      <span className="font-display text-[18px]/[1.35] text-primary transition-colors group-hover:text-accent">{block.title}</span>
      {block.description && <span className="font-display text-[14px]/[1.5] text-foreground/60">{block.description}</span>}
    </a>
  )
}

function SceneView({ block, focus }: { block: SceneBlock; focus: FocusAttrs }) {
  const r = useReader()
  const nodes = useHtmlNodes(block.id, block.html)
  const image = block.mode === 'image' && block.src ? block.src : null
  const anchors = unfoldAnchorsIn(block.html).filter((a) => r.openUnfolds.has(a) && r.unfolds.has(a))
  // Edge to edge, half height, the line centred; no eyebrow label (Niki, 10/1).
  // Scenes saved full height read at half. The ink follows the colour (lib/sceneColors).
  const ink = image ? null : sceneInk(block.color)
  return (
    <section
      {...focus}
      aria-label="Scene"
      data-ink={ink === DARK_INK ? 'dark' : 'light'}
      className="wr-bleed wr-r-scene relative clear-both my-12 flex min-h-[300px] items-center justify-center overflow-hidden sm:min-h-[360px]"
      style={{ background: image ? 'var(--scrim)' : block.color || DEFAULT_SCENE_COLOR, color: ink ?? 'var(--on-scrim)' }}
    >
      {image && (
        <>
          <FramedImage src={image} frame={block.frame} className="absolute inset-0" />
          <div aria-hidden className={SCENE_SHADE} />
        </>
      )}
      <div className="relative flex w-full max-w-[816px] flex-col items-center gap-2.5 px-6 py-12 text-center sm:px-10">
        {nodes.length > 0 && (
          <h2
            data-take={block.id}
            className="wr-prose m-0 max-w-[680px] text-[30px]/[1.1] font-normal tracking-[-0.01em] text-inherit [text-wrap:balance] sm:text-[40px]/[1.1]"
          >
            {nodes}
          </h2>
        )}
        {anchors.length > 0 && (
          <div className="flex w-full max-w-[680px] flex-col gap-2.5 text-left text-foreground">
            {anchors.map((a) => (
              <UnfoldCard key={a} block={r.unfolds.get(a)!} />
            ))}
          </div>
        )}
      </div>
    </section>
  )
}

/* ---------------------------------------------------------------- margins */

/** Right-margin items of one anchor, stacked (wide); inline under it below 1024px. */
function MarginStack({ items, inline = false }: { items: MarginBlock[]; inline?: boolean }) {
  const r = useReader()
  return (
    <div className={`${inline ? '' : 'wr-aside-right '}wr-r-aside flex flex-col gap-4`}>
      {items.map((m) =>
        m.type === 'voice' ? (
          <VoiceMemoView key={m.id} block={m} authorName={r.authorName} defaultOpen={m.id === r.firstVoiceId} />
        ) : m.type === 'margin' ? (
          <MarginNoteView key={m.id} block={m} />
        ) : null,
      )}
    </div>
  )
}

function SmallSource({ block }: { block: ThoughtLikeBlock }) {
  if (!block.source && !block.own) return null
  const label = thoughtSourceLabel(block)
  const cls = 'font-sans text-[11px] font-medium text-muted-foreground'
  return block.source?.url ? (
    <a href={block.source.url} target="_blank" rel="noopener noreferrer" className={`${cls} no-underline hover:underline`}>
      {label}
    </a>
  ) : (
    <span className={cls}>{label}</span>
  )
}

const SMALL_LABEL = 'font-sans text-[9.5px] font-semibold tracking-[0.08em] text-muted-foreground'

/** A margin note's words, with their links when the author linked some (cleaned again here, links only). */
function noteNodes(block: MarginNoteBlock, marks: readonly string[]): ReactNode[] {
  return block.html ? inlineHtmlNodes(noteHtml(block.html), marks) : plainTextNodes(block.text, marks)
}

/** Links in a note's words: the accent, underlined, as in the article's paragraphs. */
const NOTE_LINKS = '[&_a]:text-primary [&_a]:underline [&_a]:decoration-primary/40 [&_a]:underline-offset-2 hover:[&_a]:decoration-primary'

function MarginNoteView({ block }: { block: MarginNoteBlock }) {
  const { litNote } = useReader()
  const marks = useLineMarks(block.id)
  const nodes = useMemo(() => noteNodes(block, marks), [block, marks])
  const lit = litNote === block.id
  return (
    <div
      id={marginNoteId(block.id)}
      role="note"
      aria-label="Margin note"
      {...(lit ? { 'data-lit': '' } : {})}
      className={`flex scroll-my-24 flex-col gap-2 rounded-2xl border bg-card px-4 pb-3.5 pt-4 transition-[border-color,box-shadow] duration-300 ${
        lit ? 'border-primary shadow-[0_0_0_3px_color-mix(in_srgb,var(--accent)_30%,transparent)]' : 'border-card-edge'
      }`}
    >
      <span className={SMALL_LABEL}>MARGIN NOTE</span>
      <p data-take={block.id} className={`m-0 font-serif text-[16px]/[1.5] italic text-foreground wide:text-[14.5px]/[1.45] ${NOTE_LINKS}`}>
        {nodes}
      </p>
      <SmallSource block={block} />
    </div>
  )
}

/** A pinned margin note: the pin's card, as pins have always looked, sticky beside its range. */
function PinCard({ pin }: { pin: MarginNoteBlock }) {
  const { litNote } = useReader()
  const marks = useLineMarks(pin.id)
  const nodes = useMemo(() => noteNodes(pin, marks), [pin, marks])
  // Pinned from selected words: those words bring it into view and light it, as for any margin note.
  const lit = litNote === pin.id
  return (
    <div id={marginNoteId(pin.id)} role="note" aria-label="Pinned note" className="wr-r-pin flex scroll-my-24 flex-col gap-2">
      <span className={`${SMALL_LABEL} flex items-center gap-1.5`}>
        <span aria-hidden className="size-[7px] rounded-full bg-primary" />
        PINNED
      </span>
      <div
        {...(lit ? { 'data-lit': '' } : {})}
        className={`flex flex-col gap-2 rounded-2xl border bg-card px-4 pb-3.5 pt-4 transition-[border-color,box-shadow] duration-300 ${
          lit ? 'border-primary shadow-[0_0_0_3px_color-mix(in_srgb,var(--accent)_30%,transparent)]' : 'border-card-edge'
        }`}
      >
        <p data-take={pin.id} className={`m-0 font-display text-[16px]/[1.5] text-foreground/85 wide:text-[15px]/[1.5] ${NOTE_LINKS}`}>
          {nodes}
        </p>
        <SmallSource block={pin} />
      </div>
    </div>
  )
}

function UnfoldCard({ block }: { block: UnfoldBlock }) {
  const r = useReader()
  const marks = useLineMarks(block.id)
  const nodes = useMemo(() => plainTextNodes(block.text, marks), [block.text, marks])
  const words = block.words.replace(/^[\s“”"‘’']+|[\s“”"‘’']+$/g, '')
  const close = () => {
    r.closeUnfold(block.anchor)
    // Back to the words it hangs from, so keyboard readers don't lose their place.
    document.querySelector<HTMLElement>(`[data-unfold="${block.anchor}"][role="button"]`)?.focus()
  }
  return (
    <div
      id={unfoldCardId(block.anchor)}
      role="note"
      aria-label={`Unfolded: ${words}`}
      className="wr-r-unfold relative -mt-3.5 mb-[26px] flex flex-col gap-2 rounded-2xl border border-card-edge bg-card px-4 py-3.5 text-left font-sans"
    >
      <div className="flex items-start justify-between gap-3">
        <span className={SMALL_LABEL}>UNFOLDED · “{words}”</span>
        <button type="button" onClick={close} className="shrink-0 text-[11px] font-medium text-muted-foreground hover:text-foreground">
          Close
        </button>
      </div>
      <p data-take={block.id} className="m-0 font-display text-[16px]/[1.5] text-foreground/85 sm:text-[15px]/[1.5]">
        {nodes}
      </p>
      <SmallSource block={block} />
    </div>
  )
}
